import { useSEO } from "@/hooks/useSEO";
import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { motion } from "framer-motion";
import { Star, CheckCircle2 } from "lucide-react";

export default function Review() {
  useSEO({ title: "Rate Your Rental", description: "Share your Easy Drive Car Rental experience in Nassau, Bahamas.", path: "/review", noindex: true });
  const { bookingId } = useParams();
  const [email, setEmail] = useState("");
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [text, setText] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setError("");
    setLoading(true);
    try {
      await api.post("/reviews", { booking_id: bookingId, email: email.trim(), rating, text });
      setDone(true);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="review-page" className="max-w-lg mx-auto px-6 pt-36 pb-24">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <img src="/logo.png" alt="Easy Drive Car Rental" className="h-20 w-auto object-contain mb-6" />
        <p className="text-[11px] font-ui tracking-[0.3em] uppercase text-primary">Thank you for driving with us</p>
        <h1 className="mt-3 font-display text-4xl sm:text-5xl tracking-tight">
          Rate your <span className="italic gold-text">rental</span>
        </h1>
        {done ? (
          <div data-testid="review-success" className="mt-8 border border-emerald-400/40 bg-emerald-400/5 rounded-2xl p-6">
            <p className="font-ui font-semibold text-base text-emerald-600 flex items-center gap-2"><CheckCircle2 size={18} /> Review received</p>
            <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">
              Thank you! Once our team approves it, your feedback reaches the team directly. Want it on the homepage? <a href="https://search.google.com/local/writereview?placeid=ChIJ68B-Jc_t4wkRe0vyPrxW3-g" target="_blank" rel="noopener noreferrer" className="gold-text hover:underline">Leave a Google review</a> and it appears automatically.
            </p>
            <Link to="/" className="inline-block mt-4 text-sm font-ui gold-text hover:underline">Back to homepage →</Link>
          </div>
        ) : (
          <div className="mt-8 space-y-6">
            <a
              data-testid="review-google-cta"
              href="https://search.google.com/local/writereview?placeid=ChIJ68B-Jc_t4wkRe0vyPrxW3-g"
              target="_blank"
              rel="noopener noreferrer"
              className="block rounded-2xl border border-primary/30 bg-card p-5 text-center transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_12px_30px_rgba(199,30,46,0.15)]"
            >
              <div className="flex justify-center gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star key={n} size={20} className="fill-amber-400 text-amber-400" />
                ))}
              </div>
              <p className="mt-2 font-ui font-semibold text-base">Review us on Google — takes 30 seconds</p>
              <p className="text-xs text-muted-foreground font-ui mt-1">Your review shows on Google Maps &amp; our homepage automatically</p>
            </a>
            <p className="text-center text-[11px] font-ui uppercase tracking-[0.25em] text-muted-foreground">or share private feedback</p>
            <div>
              <Label className="font-ui text-sm tracking-wide text-muted-foreground">Your rating</Label>
              <div className="mt-2 flex gap-1.5">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    data-testid={`review-star-${n}`}
                    type="button"
                    onMouseEnter={() => setHover(n)}
                    onMouseLeave={() => setHover(0)}
                    onClick={() => setRating(n)}
                    aria-label={`${n} star${n > 1 ? "s" : ""}`}
                    className="transition-transform duration-150 hover:scale-125"
                  >
                    <Star size={34} className={(hover || rating) >= n ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40"} />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="font-ui text-sm tracking-wide text-muted-foreground">Email used at booking</Label>
              <Input data-testid="review-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input-dark mt-2 h-12 text-base rounded-2xl px-4" />
            </div>
            <div>
              <Label className="font-ui text-sm tracking-wide text-muted-foreground">Tell us about your trip</Label>
              <textarea
                data-testid="review-text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={4}
                maxLength={600}
                placeholder="How was the car, the handover, the island?"
                className="input-dark mt-2 w-full rounded-2xl px-4 py-3 text-base"
              />
            </div>
            {error && <p data-testid="review-error" className="text-base text-destructive font-ui">{error}</p>}
            <Button
              data-testid="review-submit-button"
              disabled={loading || !rating || !email.trim() || !text.trim()}
              onClick={submit}
              className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui h-12 text-base tracking-wide transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_12px_30px_rgba(199,30,46,0.35)]"
            >
              {loading ? "Sending…" : "Submit Review"}
            </Button>
          </div>
        )}
      </motion.div>
    </div>
  );
}
